import React, { useRef, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';

// Animated gold strings that ripple when the cursor crosses them.
export default function Strings({ density = 7, opacity = 0.55 }) {
  const canvasRef = useRef(null);
  const { stringsOn, soundOn, theme } = useTheme();
  const state = useRef({ mouse: { x: -999, y: -999 }, t: 0, raf: 0, strings: [], audioCtx: null, lastPluck: {} });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let w = 0, h = 0, dpr = Math.min(window.devicePixelRatio || 1, 2);

    const build = () => {
      w = canvas.offsetWidth; h = canvas.offsetHeight;
      canvas.width = w * dpr; canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const n = density;
      state.current.strings = Array.from({ length: n }, (_, i) => ({
        baseY: (h * (i + 1)) / (n + 1) + (i - n / 2) * 10,
        slope: (i - n / 2) * 0.012,
        amp: 0,
        vel: 0,
        phase: Math.random() * Math.PI * 2,
        thick: 0.6 + Math.random() * 0.9,
      }));
    };
    build();
    window.addEventListener('resize', build);

    const pluck = (idx) => {
      if (!soundOn) return;
      const now = performance.now();
      if (now - (state.current.lastPluck[idx] || 0) < 180) return;
      state.current.lastPluck[idx] = now;
      try {
        if (!state.current.audioCtx) state.current.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const ac = state.current.audioCtx;
        const o = ac.createOscillator();
        const g = ac.createGain();
        const freqs = [196, 233, 261, 311, 349, 392, 466];
        o.type = 'triangle';
        o.frequency.value = freqs[idx % freqs.length];
        g.gain.setValueAtTime(0.0001, ac.currentTime);
        g.gain.exponentialRampToValueAtTime(0.14, ac.currentTime + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + 1.1);
        o.connect(g); g.connect(ac.destination);
        o.start(); o.stop(ac.currentTime + 1.2);
      } catch (e) { console.debug('pluck audio unavailable', e); }
    };

    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      const mx = e.clientX - r.left, my = e.clientY - r.top;
      state.current.mouse = { x: mx, y: my };
      state.current.strings.forEach((s, i) => {
        const y = s.baseY + (mx - w / 2) * s.slope;
        if (Math.abs(my - y) < 14 && Math.abs(s.vel) < 0.5) {
          s.vel += (my > y ? -1 : 1) * 6;
          pluck(i);
        }
      });
    };
    window.addEventListener('mousemove', onMove);

    const goldLight = 'rgba(243,217,138,';
    const render = () => {
      state.current.t += 0.016;
      ctx.clearRect(0, 0, w, h);
      const t = state.current.t;
      state.current.strings.forEach((s) => {
        // spring back
        s.vel += -s.amp * 0.08;
        s.vel *= 0.95;
        s.amp += s.vel;
        const drift = Math.sin(t * 0.8 + s.phase) * 1.2;
        ctx.beginPath();
        for (let x = 0; x <= w; x += 8) {
          const prog = x / w;
          const envelope = Math.sin(prog * Math.PI);
          const y = s.baseY + (x - w / 2) * s.slope + (s.amp + drift) * envelope;
          if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        const grad = ctx.createLinearGradient(0, 0, w, 0);
        const a = opacity * (0.5 + Math.min(1, Math.abs(s.amp) / 10) * 0.5);
        grad.addColorStop(0, goldLight + '0)');
        grad.addColorStop(0.5, goldLight + a + ')');
        grad.addColorStop(1, goldLight + '0)');
        ctx.strokeStyle = grad;
        ctx.lineWidth = s.thick;
        ctx.shadowColor = 'rgba(224,183,85,0.6)';
        ctx.shadowBlur = 6;
        ctx.stroke();
      });
      state.current.raf = requestAnimationFrame(render);
    };
    if (stringsOn) render();

    return () => {
      cancelAnimationFrame(state.current.raf);
      window.removeEventListener('resize', build);
      window.removeEventListener('mousemove', onMove);
    };
  }, [stringsOn, soundOn, density, opacity, theme]);

  if (!stringsOn) return null;
  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 2 }} />;
}
